import { useCallback, useMemo } from "react";
import FeatureFlagGuard from "../../../../FeatureFlagGuard/FeatureFlagGuard";
import PermissionCollapseItem from "./PermissionCollapseItem";
import { computeCascadeToggle, filterCategories } from "./utils";
import type {
  PermissionCategory,
  PermissionNode,
  PermissionState,
} from "../../types";
import styles from "./PermissionCollapse.module.css";

interface PermissionCollapseProps {
  categories: PermissionCategory[];
  permissions: PermissionState;
  onPermissionsChange: (updated: PermissionState) => void;
  disabled?: boolean;
  filterKeys?: string[];
  devOnlyKeys?: string[];
}

function PermissionCollapse({
  categories,
  permissions,
  onPermissionsChange,
  disabled = false,
  filterKeys = [],
  devOnlyKeys = [],
}: PermissionCollapseProps) {
  const filteredCategories = useMemo(
    () => filterCategories(categories, filterKeys),
    [categories, filterKeys],
  );

  const devOnlySet = useMemo(() => new Set(devOnlyKeys), [devOnlyKeys]);

  const handleToggle = useCallback(
    (key: string, newValue: boolean) => {
      const updated = computeCascadeToggle(
        categories,
        key,
        newValue,
        permissions,
      );
      onPermissionsChange(updated);
    },
    [categories, permissions, onPermissionsChange],
  );

  if (!filteredCategories.length) return null;

  return (
    <div className={styles.container}>
      {filteredCategories.map((cat) => {
        const node: PermissionNode = {
          key: cat.category_key,
          display_name: cat.display_name,
          items: cat.items,
        };

        const item = (
          <PermissionCollapseItem
            key={cat.category_key}
            node={node}
            level={0}
            permissions={permissions}
            onToggle={handleToggle}
            disabled={disabled}
            devOnlyKeys={devOnlySet}
          />
        );

        if (devOnlySet.has(cat.category_key)) {
          return (
            <FeatureFlagGuard
              key={cat.category_key}
              mode="conditional"
              developmentMode
            >
              {item}
            </FeatureFlagGuard>
          );
        }

        return item;
      })}
    </div>
  );
}

export default PermissionCollapse;
